import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from 'components/display/Table/types';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import GenericBadge from 'components/ui/Badge';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { QuoteRow } from 'features/quote-view/types/interfaces';
import { QuoteTableRowKey } from 'features/quote-view/types/enums';
import { Status } from 'types/optim/status';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { QuoteFetchParams } from 'slices/generic-table/hooks/useQuoteTable';
import { mapQuoteSummaries } from 'features/quote-view/mappers/mapQuoteSummaryToQuote';
import { GetQuoteListResponseDto, GetGenericListCommandDto } from 'api/quote';
import { quotesAPI } from 'services/swagger-api';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { IdLabel } from 'api/risk';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ReceiptText } from 'lucide-react';
import {
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { useDocumentViewerDialog } from 'hooks/useDocumentViewerDialog';
import { DocumentScopeEnum } from 'api/documents';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

interface PersonQuotesProps {
  personId: string;
}

export const PersonQuotes: React.FC<PersonQuotesProps> = ({ personId }) => {
  const { t } = useDynamicTranslation([
    NsKey.Common,
    NsKey.Quote,
    NsKey.Risk,
    NsKey.Pricing,
    NsKey.Contract
  ]);
  const navigate = useNavigate();
  const { createMenuItem } = useActionMenu();
  const { openDocumentViewer } = useDocumentViewerDialog();
  // Mêmes droits que la liste devis globale (matrice centrale) : Courtier only.
  const canOpenProcess = useRoleCan(Capability.QuoteOpen);
  const canRowActions = useRoleCan(Capability.QuoteActions);

  const goToContractualizationStep = useCallback(
    (riskId: string, quoteId: string) => {
      if (!canOpenProcess) return;
      navigate(`/simulation/contractualisation/${riskId}/${quoteId}`);
    },
    [navigate, canOpenProcess]
  );

  const columns: Column<QuoteRow>[] = useMemo(
    () => [
      {
        key: QuoteTableRowKey.DATE,
        label: t('COMMON.DATE'),
        sortable: true,
        render: (value) => {
          const date = new Date(value as string);
          const formattedDate = format(date, 'dd MMM yyyy', { locale: fr });
          const formattedTime = format(date, 'HH:mm');

          return (
            <div className="text-left">
              <div className="text-sm font-medium">{formattedDate}</div>
              <div className="text-xs text-gray-500">{formattedTime}</div>
            </div>
          );
        }
      },
      {
        key: QuoteTableRowKey.QUOTE_IDENTIFIER,
        label: t('QUOTE.LABELS.QUOTE_NUMBER'),
        render: (value, row) => {
          const quoteIdentifier = value as IdLabel;
          return (
            <div
              onClick={
                canOpenProcess
                  ? () =>
                      quoteIdentifier.id &&
                      goToContractualizationStep(row.riskId, quoteIdentifier.id)
                  : undefined
              }
              className={
                canOpenProcess
                  ? 'cursor-pointer font-bold text-primary hover:underline'
                  : 'font-bold text-gray-700'
              }
            >
              {quoteIdentifier && quoteIdentifier.label}
            </div>
          );
        }
      },
      {
        key: QuoteTableRowKey.PRODUCT,
        label: t('COMMON.LABELS.PRODUCT')
      },
      {
        key: QuoteTableRowKey.SUPPORT,
        label: t('COMMON.LABELS.SUPPORT')
      },
      {
        key: QuoteTableRowKey.OWNER,
        label: t('COMMON.LABELS.INSURER')
      },
      {
        key: QuoteTableRowKey.CLIENT_PRICE,
        label: t('PRICING.LABELS.CLIENT_PRICE')
      },
      {
        key: QuoteTableRowKey.STATUS,
        label: t('COMMON.STATUS'),
        sortable: true,
        render: (value) => {
          const status = value as Status;
          return (
            <GenericBadge
              label={status.label}
              color={status.color}
              badgeType="sm-bold"
            />
          );
        }
      }
    ],
    [t, goToContractualizationStep, canOpenProcess]
  );

  const getMoreMenuActions = useCallback(
    (row: QuoteRow) => [
      {
        options: {
          icon: 'fileText'
        },
        key: 'viewDocuments',
        label: t('CONTRACT.ACTIONS.VIEW_DOCUMENTS'),
        action: () => {
          openDocumentViewer({
            strategy: DocumentScopeEnum.Quote,
            entityId: row.quoteIdentifier.id
          });
        }
      },
      {
        options: {
          icon: 'arrowRightLeft'
        },
        key: 'transformToContract',
        label: t('QUOTE.ACTIONS.TRANSFORM_TO_CONTRACT'),
        action: () => {
          goToContractualizationStep(row.riskId, row.quoteIdentifier.id);
        }
      }
    ],
    [t, openDocumentViewer, goToContractualizationStep]
  );

  const getMenuItems = useCallback(
    (row: QuoteRow) => {
      return getMoreMenuActions(row).map((action) =>
        createMenuItem(action.key, action.label, action.action, action.options)
      );
    },
    [getMoreMenuActions, createMenuItem]
  );

  const actions = useMemo(
    () => ({
      row: [
        {
          label: 'seeMore',
          render: (row: QuoteRow, index: number) => (
            <Button
              key={index}
              color="slate"
              round
              onClick={() =>
                goToContractualizationStep(row.riskId, row.quoteIdentifier.id)
              }
            >
              <EyeIcon />
            </Button>
          )
        },
        {
          label: 'moreMenu',
          render: (row: QuoteRow, index: number) => (
            <DropdownButton
              key={index}
              width="w-64"
              anchor="bottom"
              items={getMenuItems(row)}
            />
          )
        }
      ]
    }),
    [goToContractualizationStep, getMenuItems]
  );

  const fetchQuotes = useCallback(
    async (params: QuoteFetchParams): Promise<TableResponse<QuoteRow>> => {
      try {
        const command: GetGenericListCommandDto = {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          sorting: params.sorting as any,
          filters: {
            personIds: [personId]
          }
        };

        const result: GetQuoteListResponseDto = await quotesAPI.getQuotes({
          getGenericListCommandDto: command
        });

        const mappedQuotes = mapQuoteSummaries(result.items || []);

        return {
          items: mappedQuotes,
          totalItems: result.totalCount || mappedQuotes.length,
          totalPages: Math.ceil(
            (result.totalCount || mappedQuotes.length) / params.pageSize
          ),
          currentPage: params.pageNumber
        };
      } catch (error) {
        console.error('Erreur lors de la récupération des devis:', error);
        throw error;
      }
    },
    [personId]
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      <div className="flex items-center gap-3 p-4 border-b">
        <ReceiptText className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-gray-900">
          {t('FEATURES.TITLE.MY_QUOTES')}
        </h3>
      </div>
      <div className="p-4">
        <ServerTable<QuoteRow>
          apiConfig={{
            fetchData: fetchQuotes
          }}
          columns={columns}
          actions={canRowActions ? actions : undefined}
          onRowClick={(row) =>
            goToContractualizationStep(row.riskId, row.quoteIdentifier.id)
          }
        />
      </div>
    </div>
  );
};
